import type { EntryDraft, Storage, Track, TrackValue } from '@anytracker/core'
import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * A partly-filled form that survives leaving the screen (T-304).
 *
 * Not a nicety. Phones interrupt constantly — a call, a notification, the
 * browser reclaiming a background tab — and an app that loses a half-filled
 * check-in once will not be trusted with the next one. That is a data problem,
 * not a UX one: the entry that never gets re-entered is a gap in the record
 * indistinguishable from a day nothing happened.
 *
 * Persisted rather than held in memory, because the tab being discarded is
 * exactly the case component state does not survive.
 */

/** What is written to storage. Values only — tracks are re-resolved on load. */
interface StoredDraft {
  readonly at: { epochMs: number; timeZone: string }
  readonly tagIds: readonly string[]
  readonly note?: string
  readonly values: Record<string, TrackValue>
  readonly slotNotes: Record<string, string>
}

export interface DraftState {
  readonly at: { epochMs: number; timeZone: string }
  readonly tagIds: readonly string[]
  readonly note: string
  readonly values: Record<string, TrackValue>
  readonly slotNotes: Record<string, string>
}

export function draftKey(templateId: string | null): string {
  return `draft.${templateId ?? 'adhoc'}`
}

/**
 * Only the values are stored, never the resolved tracks.
 *
 * A draft saved on Monday and reopened on Friday must reflect Friday's track
 * configuration — a serialised `Track` would resurrect a scale the user has
 * since changed, and quietly record against it.
 */
function serialise(draft: DraftState): StoredDraft {
  return {
    at: draft.at,
    tagIds: draft.tagIds,
    ...(draft.note === '' ? {} : { note: draft.note }),
    values: draft.values,
    slotNotes: draft.slotNotes,
  }
}

export function useDraft(
  storage: Storage,
  templateId: string | null,
  initial: () => DraftState,
): {
  readonly draft: DraftState | null
  readonly update: (changes: Partial<DraftState>) => void
  readonly setValue: (trackId: string, value: TrackValue | undefined) => void
  readonly clear: () => Promise<void>
  readonly restored: boolean
} {
  const key = draftKey(templateId)
  const [draft, setDraft] = useState<DraftState | null>(null)
  const [restored, setRestored] = useState(false)
  const loaded = useRef(false)

  useEffect(() => {
    let cancelled = false
    async function load() {
      const raw = await storage.settings.get(key)
      if (cancelled) {
        return
      }
      if (raw === undefined) {
        setDraft(initial())
        loaded.current = true
        return
      }
      try {
        const stored = JSON.parse(raw) as StoredDraft
        setDraft({
          at: stored.at,
          tagIds: stored.tagIds,
          note: stored.note ?? '',
          values: stored.values,
          slotNotes: stored.slotNotes,
        })
        setRestored(true)
      } catch {
        // A draft that will not parse is worth losing silently; refusing to
        // open the form over it would be worse than starting fresh.
        setDraft(initial())
      }
      loaded.current = true
    }
    void load()
    return () => {
      cancelled = true
    }
    // `initial` is intentionally not a dependency: it stamps the current time,
    // so including it would reset the draft on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, storage])

  // Written on every change rather than on a timer. The interruption this
  // guards against gives no warning, so there is no safe moment to defer to.
  useEffect(() => {
    if (!loaded.current || draft === null) {
      return
    }
    void storage.settings.set(key, JSON.stringify(serialise(draft)))
  }, [draft, key, storage])

  const update = useCallback((changes: Partial<DraftState>) => {
    setDraft((current) => (current === null ? current : { ...current, ...changes }))
  }, [])

  const setValue = useCallback((trackId: string, value: TrackValue | undefined) => {
    setDraft((current) => {
      if (current === null) {
        return current
      }
      const values = { ...current.values }
      if (value === undefined) {
        // Deleted, not set to a falsy value: a track skipped and a track
        // recorded as zero are different claims (ADR-0005).
        delete values[trackId]
      } else {
        values[trackId] = value
      }
      return { ...current, values }
    })
  }, [])

  const clear = useCallback(async () => {
    await storage.settings.remove(key)
    loaded.current = false
    setDraft(null)
    setRestored(false)
  }, [key, storage])

  return { draft, update, setValue, clear, restored }
}

/** Drops values for tracks that are no longer in the form. */
export function prune(
  values: Record<string, TrackValue>,
  tracks: readonly Track[],
): Record<string, TrackValue> {
  const live = new Set(tracks.map((track) => track.id))
  return Object.fromEntries(
    Object.entries(values).filter(([trackId]) => live.has(trackId)),
  )
}

/** An entry's draft, as `EntryDraft` expects it. Unset slots stay undefined. */
export function toEntryDraft(
  draft: DraftState,
  templateId: string | null,
  tracks: readonly Track[],
): EntryDraft {
  return {
    templateId: templateId ?? '',
    at: draft.at,
    tagIds: draft.tagIds,
    slots: tracks.map((track) => ({
      track,
      value: draft.values[track.id],
      archived: track.archived,
    })),
    missingTrackIds: [],
  }
}
